import { Stack } from '@chakra-ui/react';
import { Cart } from '@diamond/shared/types';
import { CardPromoInfo, CardPromoInfoSkeleton } from '@diamond/shared/ui';
import { promoValidFromToFormat } from '@diamond/shared/utils';
import { formatDiscountDisplay } from '@diamond/sol-buyer/utils';
import { useCartStore, useCartTotal } from '@diamond/sol-buyer-context';
import { useMemo } from 'react';

interface CartPromosSummaryProps {
  cartItems: Cart[];
  selectedIds: Set<string>;
  isLoading?: boolean;
}

export const CartPromosSummary = ({
  cartItems,
  selectedIds,
  isLoading: externalLoading,
}: CartPromosSummaryProps) => {
  const { isFetching: isCartTotalFetching } = useCartTotal();
  const isLoading = externalLoading || isCartTotalFetching;

  const freeGoodsPromoRecord = useCartStore(
    (state) => state.freeGoodsPromoRecord
  );
  const discountPromoRecord = useCartStore(
    (state) => state.discountPromoRecord
  );

  const selectedItems = useMemo(
    () => cartItems.filter((item) => selectedIds.has(item.id)),
    [cartItems, selectedIds]
  );

  const allPromos = useMemo(() => {
    const promosMap = new Map<string, any>();

    selectedItems.forEach((item) => {
      const freeGoodsPromos = freeGoodsPromoRecord[item.product.id] || [];
      const discountPromos = discountPromoRecord[item.product.id] || [];

      discountPromos.forEach((promo) => {
        const promoId = promo.id;
        if (!promosMap.has(promoId)) {
          const isAppliesInMultiples =
            'is_applies_in_multiples' in promo &&
            typeof (promo as { is_applies_in_multiples?: boolean })
              .is_applies_in_multiples === 'boolean' &&
            (promo as { is_applies_in_multiples?: boolean })
              .is_applies_in_multiples;
          const mechanismText = `${promo.mechanism ?? ''} ${
            isAppliesInMultiples ? '(Berlaku kelipatan)' : ''
          }`.trim();

          let discountDisplay: string | undefined;
          if (
            typeof promo.deduction_amount === 'number' &&
            typeof promo.is_discount === 'boolean'
          ) {
            discountDisplay = formatDiscountDisplay(
              promo.deduction_amount,
              promo.is_discount
            );
          }

          promosMap.set(promoId, {
            type: 'discount',
            duration: promoValidFromToFormat(
              promo.valid_from,
              promo.valid_until
            ),
            text: mechanismText,
            promoId,
            discountDisplay,
            itemId: item.id,
            isDisabled: item.product.stock === 0,
          });
        }
      });

      freeGoodsPromos.forEach((promo) => {
        const promoId = promo.id;
        if (!promosMap.has(promoId)) {
          const images = promo.product_image_thumbnails || [];
          const freeQty = promo.calculatedFreeQty / 1000;
          const price = promo.price;
          const savings = price * freeQty;
          const isAppliesInMultiples =
            'is_applies_in_multiples' in promo &&
            typeof promo.is_applies_in_multiples === 'boolean' &&
            promo.is_applies_in_multiples;
          promosMap.set(promoId, {
            type: 'free-item',
            duration: promoValidFromToFormat(
              promo.valid_from,
              promo.valid_until
            ),
            text: `${promo.mechanism} ${
              isAppliesInMultiples ? '(Berlaku kelipatan)' : ''
            }`.trim(),
            promoId,
            item: {
              id: promo.product_id,
              price: promo.price,
              img: images[0],
              qty: freeQty,
              uom: promo.sales_unit_desc,
            },
            images: images.length > 0 ? images : undefined,
            savings: savings > 0 ? savings : undefined,
            itemId: item.id,
            isDisabled: item.product.stock === 0,
          });
        }
      });
    });

    return Array.from(promosMap.values());
  }, [selectedItems, freeGoodsPromoRecord, discountPromoRecord]);

  if (isLoading && selectedIds.size > 0) {
    return (
      <Stack spacing="4" mt="4">
        <CardPromoInfoSkeleton />
        <CardPromoInfoSkeleton />
      </Stack>
    );
  }

  if (allPromos.length === 0) {
    return null;
  }

  return (
    <Stack spacing="4" mt="4">
      {allPromos.map((promoItem) => {
        if (promoItem.type === 'discount') {
          return (
            <CardPromoInfo
              key={promoItem.promoId}
              type="discount"
              duration={promoItem.duration}
              text={promoItem.text}
              discountDisplay={promoItem.discountDisplay}
              images={promoItem.images}
              isInvalid={promoItem.isDisabled}
            />
          );
        }
        return (
          <CardPromoInfo
            key={promoItem.promoId}
            type="free-item"
            duration={promoItem.duration}
            text={promoItem.text}
            item={promoItem.item}
            images={promoItem.images}
            savings={promoItem.savings}
            isInvalid={promoItem.isDisabled}
            showFreeItemPrice={false}
          />
        );
      })}
    </Stack>
  );
};
